<template>
  <div class="consumables">
    <OmxList
        :data="filteredConsumablesData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasSearch ? 'filter-variant' : 'package-variant'"
        :empty-title="hasSearch ? $t('omx-list.empty-title') : $t('consumables-view-empty.title')"
        :empty-body="hasSearch ? null : $t('consumables-view-empty.body')"
        :skeleton-columns="[1.6, 1, 1, 0.8, 0.8, 0.8, 1.6]"
    >
      <template #actions>
        <OmxPermissionButton :allowed="canCreate" type="is-primary" icon-left="plus-thick" @click="addNewConsumableClicked">
          {{ $t('consumables-view.new-consumable-button') }}
        </OmxPermissionButton>
      </template>

      <template #empty-actions>
        <OmxPermissionButton v-if="!hasSearch" :allowed="canCreate" type="is-primary" icon-left="plus-thick" @click="addNewConsumableClicked">
          {{ $t('consumables-view.new-consumable-button') }}
        </OmxPermissionButton>
        <b-button v-if="hasSearch" @click="search = ''">{{ $t('omx-list.clear-selection') }}</b-button>
      </template>

      <b-table-column field="consumable.resource" :label="$t('consumables-view.consumable-resource-column')" v-slot="props" sortable>
        <router-link
            :to="{ name: 'ConsumableDetails', params: { project_id: project_id, consumable_id: props.row.consumable.document_id, is_template: is_template }}"
            class="omx-row-title"
        >{{ getAdjustedTitle(props.row.consumable.resource) }}</router-link>
      </b-table-column>

      <b-table-column field="consumable.category" :label="$t('consumables-view.consumable-category-column')" v-slot="props" sortable>
        {{ props.row.consumable.category }}
      </b-table-column>

      <b-table-column field="consumable.provider" :label="$t('consumables-view.consumable-provider-column')" v-slot="props" sortable>
        {{ props.row.consumable.provider }}
      </b-table-column>

      <b-table-column field="consumable.meta.estimation.quantity" :label="$t('consumables-view.consumable-quantity-column')" v-slot="props" sortable>
        {{ getFormattedQuantity(props.row.consumable.meta.estimation, props.row.consumable.unit) }}
      </b-table-column>

      <b-table-column field="consumable.meta.estimation.fee_per_unit" :label="$t('consumables-view.consumable-fee-per-unit-column')" v-slot="props" sortable>
        {{ getFormattedFeePerUnit(props.row.consumable.meta.estimation) }}
      </b-table-column>

      <b-table-column field="consumable.meta.estimation.expected_total_fee" :label="$t('consumables-view.consumable-total-fee-column')" v-slot="props" sortable>
        {{ getFormattedPricing(props.row.estimation.expected_total_fee) }}
      </b-table-column>

      <b-table-column field="text" :label="$t('consumables-view.consumable-text-column')" v-slot="props">
        {{ truncateColumn(props.row.consumable.text) }}
      </b-table-column>
    </OmxList>

    <b-modal :width="640" v-model="isModalActive" trap-focus :destroy-on-hide="true" aria-role="dialog" aria-modal>
      <template #default="props">
        <ConsumableDetails :project_id="project_id" consumable_id="new" @close="props.close" @consumableAdded="readConsumablesData" />
      </template>
    </b-modal>
  </div>
</template>

<script>
import ConsumableDetails from "@/components/ConsumableDetails";
import firebase from "firebase/app";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Consumables",
  components: { OmxList, ConsumableDetails },
  props: {
    project_id: { type: String },
    is_template: { type: Boolean }
  },
  data() {
    return {
      consumablesData: [],
      isLoading: false,
      isModalActive: false,
      search: ""
    };
  },
  computed: {
    canCreate() {
      return (!this.is_template && this.currentUserAccess('ConsumableDetails', this.UserActions.CREATE, this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', this.UserActions.CREATE));
    },
    hasSearch() { return this.search.trim() !== ""; },
    filteredConsumablesData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.consumablesData;
      return this.consumablesData.filter(row => {
        const c = row.consumable || {};
        return [c.resource, c.category, c.provider, c.text].filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    }
  },
  methods: {
    readConsumablesData() {
      this.isModalActive = false;
      this.isLoading = true;
      this.consumablesData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getConsumablesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getConsumablesFunction({ action: "get_consumables", project_id: this.project_id, fill_meta: true })
          .then((result) => {
            if (result.data.consumables.length > 0) {
              result.data["consumables"].forEach((consumable) => {
                this.consumablesData.push({
                  consumable,
                  estimation: consumable.meta.estimation ? consumable.meta.estimation : {}
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(error.message);
            console.log("Error getting consumables:", error?.code);
            this.isLoading = false;
          });
    },
    addNewConsumableClicked() { this.isModalActive = true; }
  },
  mounted() {
    this.readConsumablesData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.consumables { margin-top: 16px; }
.omx-row-title {
  color: $omx-brand-600;
  font-weight: 600;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
</style>
